import { Auth } from 'aws-amplify';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { useAppContext } from '../../../contexts/AppContext';
import UnauthenticatedLayout from '../../../overwrites/views/layouts/UnauthenticatedLayout/UnauthenticatedLayout';
import { requiredClientMetadata } from '../../../utils/Amplify';
import { Form, ReSendLink } from '../auth.styles';
import SignupHeader from '../shared/SignupHeader';
import { CodeInstruction, CodeInstructionEmail, StyledTextField, SubmitButton } from './ResetPasswordPage.styles';

interface Location {
  state: any;
}

export default function ResetPassword() {
  const { reloadAuthInfo, showError, showAlert } = useAppContext();
  const [code, setCode] = React.useState('');
  const [newPassword, setNewPassword] = React.useState('');
  const [newPassword2, setNewPassword2] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const { t } = useTranslation();
  const { state }: Location = useLocation();

  function validateForm(): boolean {
    if (code.length === 0) {
      showError(t('auth.recovery.emptyCode'));
      return false;
    } else if (newPassword.length === 0) {
      showError(t('auth.recovery.newPasswordEmpty'));
      return false;
    } else if (newPassword !== newPassword2) {
      showError(t('auth.recovery.passwordsNotMatch'));
      return false;
    }
    return true;
  }

  function handleSubmit(event: any) {
    event.preventDefault();
    if (!validateForm() || loading) {
      return;
    }
    void (async () => {
      try {
        setLoading(true);
        const sanitizedEmail = state.email.trim().toLowerCase();
        await Auth.forgotPasswordSubmit(sanitizedEmail as string, code, newPassword);
        await Auth.signIn(sanitizedEmail as string, newPassword, { ...requiredClientMetadata, 'first-login': 'true' });
        await reloadAuthInfo();
      } catch (e: any) {
        showError(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }

  const resendEmail = () => {
    void (async () => {
      const sanitizedEmail = state.email.trim().toLowerCase();

      await Auth.forgotPassword(sanitizedEmail as string);
      showAlert(t('auth.recovery.emailSent'));
    })();
  };

  return (
    <UnauthenticatedLayout
      title={t('auth.recovery.newPasswordTitle')}
      header={<SignupHeader />}
      styleTitle={{ marginBottom: '8px' }}
    >
      <Form noValidate onSubmit={handleSubmit}>
        <CodeInstruction>
          <Trans
            i18nKey="auth.recovery.codeInstruction"
            values={{ email: state?.email }}
            components={{ black: <CodeInstructionEmail /> }}
          />
        </CodeInstruction>
        <StyledTextField
          name="code"
          required
          fullWidth
          autoFocus
          id="code"
          label={t('auth.recovery.recoveryCodeField')}
          value={code}
          onChange={(e: any) => setCode(e.target.value as string)}
        />
        <StyledTextField
          autoComplete="new-password"
          fullWidth
          id="password"
          label={t('auth.recovery.newPasswordField')}
          name="password"
          onChange={(e: any): void => setNewPassword(e.target.value as string)}
          required
          type="password"
          value={newPassword}
        />
        <StyledTextField
          autoComplete="re-enter-password"
          fullWidth
          id="password2"
          label={t('auth.recovery.reEnterPasswordField')}
          name="password"
          onChange={(e: any): void => setNewPassword2(e.target.value as string)}
          required
          type="password"
          value={newPassword2}
        />
        <SubmitButton type="submit" loading={loading}>
          {t('auth.recovery.newPasswordButton')}
        </SubmitButton>
        <ReSendLink onClick={resendEmail}>{t('auth.recovery.resendLink')}</ReSendLink>
      </Form>
    </UnauthenticatedLayout>
  );
}
